import { Endpoints, get } from 'utils/api';
import { IGNORED_LEGAL_TERMS_CONTENT_VERSION, USER_TERMS_ACCEPTANCE_ENABLED } from 'utils/consts';

interface LegalVersionsResponse {
  pages: Record<string, LegalPageInfo>;
  meta?: { fetchedAt: string };
}

interface LegalPageInfo {
  name: string;
  contentVersion: string;
  publishedAt: string;
}

/** GET /user/legal-terms-acceptances — we only read `items`. */
interface LegalTermsAcceptancesResponse {
  items: { legalTermsVersion: string }[];
}

/** Parse the terms version string into a record of agreements. */
export const parseLegalTermsVersionString = (termsVersion: string): Record<string, string> => {
  const agreements = termsVersion
    .split(';')
    .filter(Boolean)
    .filter((agreement) => agreement.includes('='));
  const agreementMap = agreements.map((agreement) => agreement.split('='));
  return Object.fromEntries(agreementMap);
};

const KNOWN_PAGE_KEYS = ['msa', 'privacy-policy', 'terms'];

// Versions to treat as already-accepted, even when the user has no matching acceptance record.
// Grandfathers users who accepted under the pre-per-key flow and therefore have no `msa=` segment stored.
const GRANDFATHERED_VERSIONS: Record<string, string> = {
  msa: '2025-09-30',
};

/** Build the terms version string from a record of agreements.
 *  Only known page keys are included; output is sorted alphabetically so the
 *  result is stable regardless of the key order returned by the API. */
export const buildLegalTermsVersionString = (pages: Record<string, LegalPageInfo>): string =>
  Object.entries(pages)
    .filter(([key]) => KNOWN_PAGE_KEYS.includes(key))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, { contentVersion }]) => `${key}=${contentVersion}`)
    .join(';') + ';';

export const getLegalTermsVersion = async (): Promise<string | undefined> => {
  if (!USER_TERMS_ACCEPTANCE_ENABLED) {
    return undefined;
  }
  const data = await get<LegalVersionsResponse>(Endpoints.LEGAL_VERSIONS, true);
  if (!data.pages || Object.keys(data.pages).length === 0) {
    return undefined;
  }
  return buildLegalTermsVersionString(data.pages);
};

// compares the latest terms with the user's most recent accepted terms
// returns the latest terms version string that the user needs to accept
// returns the mismatches to help the modal identify the specific terms that need to be accepted
export const getTermsUserNeedsToAccept = async (
  forceEnabled = false
): Promise<{ latestTerms: string; termsToAccept: string[] } | undefined> => {
  if (!USER_TERMS_ACCEPTANCE_ENABLED && !forceEnabled) {
    return undefined;
  }
  const [latestData, acceptances] = await Promise.all([
    get<LegalVersionsResponse>(Endpoints.LEGAL_VERSIONS, true),
    get<LegalTermsAcceptancesResponse>(Endpoints.ACCEPT_LEGAL_TERMS),
  ]);
  const pages = latestData.pages ?? {};

  if (Object.keys(pages).length === 0) {
    return undefined;
  }

  // Strip pages whose published version is grandfathered, so the version we eventually POST to
  // ACCEPT_LEGAL_TERMS doesn't claim acceptance of agreements we never displayed in the modal.
  const pagesToAccept = Object.fromEntries(
    Object.entries(pages).filter(([key, info]) => GRANDFATHERED_VERSIONS[key] !== info.contentVersion)
  );

  const latestTerms = buildLegalTermsVersionString(pagesToAccept);
  // escape hatch: lets us discard one or more published versions (e.g. test versions) without prompting all users
  // supports a comma-separated list, e.g. "v1,msa=v2;terms=v2;"
  if (IGNORED_LEGAL_TERMS_CONTENT_VERSION && !forceEnabled) {
    const ignoredVersions = IGNORED_LEGAL_TERMS_CONTENT_VERSION.split(',')
      .map((v) => v.trim())
      .filter(Boolean);
    if (ignoredVersions.includes(latestTerms)) {
      return undefined;
    }
  }

  const latestTermsMap = parseLegalTermsVersionString(latestTerms);
  const userTerms = (acceptances.items ?? [])[0]?.legalTermsVersion ?? '';
  // most recent terms that a user has accepted
  const userTermsMap = parseLegalTermsVersionString(userTerms);

  const mismatched = Object.entries(latestTermsMap).filter(([key, version]) => userTermsMap[key] !== version);
  const termsToAccept = mismatched.map(([key]) => key);

  if (termsToAccept.length === 0 && !forceEnabled) {
    return undefined;
  }
  return { latestTerms, termsToAccept };
};
